import * as React from 'react';

import { useFormContext, useWatch } from 'react-hook-form';

import {
  Box,
  Collapse,
  Divider,
  Grid,
  InputAdornment,
  Typography,
} from '@mui/material';

import { SafetyEventReport, RadioGroupOption, FormGenerator } from '../../../core/api/clients';

import ControlledCascadingSelect from '../../../components/ControlledCascadingSelect';

import ControlledTextField from '../../../components/ControlledTextField';
import ControlledTimeField from '../../../components/ControlledTimeField';
import ControlledDatePicker from '../../../components/ControlledDatePicker';
import ControlledRadioGroup from '../../../components/ControlledRadioGroup';
import ControlledSelect from '../../../components/ControlledSelect';

import { useDispatchEventFieldOptions } from '../../../core/context/DispatchEventFieldOptionsContext';
import { useSafetyEventReportData } from '../../../core/context/SafetyEventReportDataContext';

function EventStep({ visible, readOnly }: { visible: boolean, readOnly?: boolean }) {
  const { eventOptions, phaseOptions } = useDispatchEventFieldOptions();
  const { control, setValue } = useFormContext<SafetyEventReport>();
  const { allowedActions, safetyEventReport } = useSafetyEventReportData();

  const triggerFieldWatch = useWatch<SafetyEventReport>({ control, name: 'trigger' });
  const doesStringContainEmergencyDeclared = (text: string | undefined): boolean => text?.toLowerCase().includes('emergency was declared') ?? false;
  React.useEffect(() => {
    if (!safetyEventReport.emergencyDeclared && doesStringContainEmergencyDeclared(triggerFieldWatch?.toString())) {
      setValue('emergencyDeclared', RadioGroupOption.Yes);
    }
  }, [triggerFieldWatch]);

  return (
    <Collapse in={visible}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box display="flex">
            <ControlledTextField<SafetyEventReport>
              name="eventTitle"
              control={control}
              rules={{ required: true }}
              readOnly={readOnly}
              disableEditPopper
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={4}>
          <ControlledTextField<SafetyEventReport>
            name="locationOfEvent"
            label="Location of Event"
            control={control}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <ControlledDatePicker<SafetyEventReport>
            name="eventDate"
            control={control}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit || safetyEventReport.formGeneratedFrom === FormGenerator.FlightLookup}
            isEditable={safetyEventReport.formGeneratedFrom !== FormGenerator.FlightLookup}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <ControlledTimeField
            name="localEventTime"
            control={control}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12}>
          <ControlledCascadingSelect<SafetyEventReport>
            groupName="eventType"
            name="eventType"
            control={control}
            fieldNames={['eventType', 'eventType2', 'eventType3']}
            options={eventOptions}
            rules={{ required: true }}
            readOnly={readOnly}
            setValue={setValue}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
      </Grid>
      <Divider sx={{ my: 2 }} />
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <ControlledSelect<SafetyEventReport>
            name="typeOfFlight"
            label="Type of Flight"
            control={control}
            menuItems={[
              { name: 'Revenue (part 121)', value: 'Revenue (part 121)' },
              { name: 'Non-Revenue (part 91)', value: 'Non-Revenue (part 91)' },
              { name: 'Maintenance Test', value: 'Maintenance Test' },
              { name: 'Special Flight Permit', value: 'Special Flight Permit' },
            ]}
            readOnly={readOnly}
            rules={{ required: true }}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <ControlledSelect<SafetyEventReport>
            name="operationalImpact"
            control={control}
            menuItems={[
              { name: 'Air Turnback', value: 'Air Turnback' },
              { name: 'Diversion/Unscheduled Landing', value: 'Diversion/Unscheduled Landing' },
              { name: 'Gate Return', value: 'Gate Return' },
              { name: 'Rejected Takeoff', value: 'Rejected Takeoff' },
              { name: 'No Disruption', value: 'No Disruption' },
              { name: 'Flight Delay', value: 'Flight Delay' },
            ]}
            readOnly={readOnly}
            rules={{ required: true }}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <ControlledTextField<SafetyEventReport>
            name="numberOfPassengers"
            label="Number of Passengers"
            control={control}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <ControlledSelect<SafetyEventReport>
            name="locationOfPassengers"
            control={control}
            menuItems={[
              { name: 'On Aircraft', value: 'On Aircraft' },
              { name: 'Deplaned and at Gate', value: 'Deplaned and at Gate' },
              { name: 'Deplaned and Dispersed', value: 'Deplaned and Dispersed' },
            ]}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <ControlledSelect<SafetyEventReport>
            name="flightDeckObserver"
            control={control}
            menuItems={[
              { name: 'N/A', value: 'N/A' },
              { name: 'Government Agency', value: 'Government Agency' },
              { name: 'Offline', value: 'Offline' },
              { name: 'Air Wisconsin', value: 'Air Wisconsin' },
            ]}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <ControlledTextField<SafetyEventReport>
            name="flightDeckObserverInformation"
            control={control}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12}>
          <ControlledCascadingSelect<SafetyEventReport>
            groupName="phaseOfFlight"
            name="phaseOfFlight"
            control={control}
            fieldNames={['phaseOfFlight', 'phaseOfFlight2']}
            options={phaseOptions}
            rules={{ required: true }}
            readOnly={readOnly}
            setValue={setValue}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} md={8}>
          <ControlledSelect<SafetyEventReport>
            name="trigger"
            label="What triggered the event?"
            control={control}
            menuItems={[
              { name: 'A known crew safety concern', value: 'A known crew safety concern' },
              { name: 'A known passenger safety concern', value: 'A known passenger safety concern' },
              { name: 'A potential media/public relations concern', value: 'A potential media/public relations concern' },
              { name: 'Requested by On-Call Leader', value: 'Requested by On-Call Leader' },
            ]}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <ControlledRadioGroup<SafetyEventReport>
            RadioGroupProps={{ row: true }}
            name="pilotFlying"
            control={control}
            radioOptions={[
              { value: 'captain', label: 'Captain' },
              { value: 'firstOfficer', label: 'First Officer' },
            ]}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12}>
          <ControlledTextField<SafetyEventReport>
            name="narrative"
            label="Event Description"
            control={control}
            readOnly={readOnly}
            TextFieldProps={{
              multiline: true,
              rows: 6,
            }}
            rules={{ required: true }}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
      </Grid>
      <Divider sx={{ my: 2 }} />
      <Grid item xs={12} mb={1}>
        <Typography variant="h6">Considerations</Typography>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
          <ControlledRadioGroup<SafetyEventReport>
            RadioGroupProps={{ row: true }}
            control={control}
            label="Emergency Declared"
            name="emergencyDeclared"
            radioOptions={[
              { label: 'Yes', value: RadioGroupOption.Yes },
              { label: 'No', value: RadioGroupOption.No },
            ]}
            rules={{ required: true }}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
          <ControlledRadioGroup<SafetyEventReport>
            RadioGroupProps={{ row: true }}
            control={control}
            label="Passengers Briefed"
            name="passengersBriefed"
            radioOptions={[
              { label: 'Yes', value: RadioGroupOption.Yes },
              { label: 'No', value: RadioGroupOption.No },
            ]}
            rules={{ required: true }}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
          <ControlledRadioGroup<SafetyEventReport>
            RadioGroupProps={{ row: true }}
            control={control}
            label="ARFF Requested"
            name="arffRequested"
            radioOptions={[
              { label: 'Yes', value: RadioGroupOption.Yes },
              { label: 'No', value: RadioGroupOption.No },
            ]}
            rules={{ required: true }}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
          <ControlledRadioGroup<SafetyEventReport>
            RadioGroupProps={{ row: true }}
            control={control}
            label="ARFF Responded"
            name="arffResponded"
            radioOptions={[
              { label: 'Yes', value: RadioGroupOption.Yes },
              { label: 'No', value: RadioGroupOption.No },
            ]}
            rules={{ required: true }}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
          <ControlledRadioGroup<SafetyEventReport>
            RadioGroupProps={{ row: true }}
            control={control}
            label="Cabin Braced"
            name="cabinBraced"
            radioOptions={[
              { label: 'Yes', value: RadioGroupOption.Yes },
              { label: 'No', value: RadioGroupOption.No },
            ]}
            rules={{ required: true }}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
          <ControlledRadioGroup<SafetyEventReport>
            RadioGroupProps={{ row: true }}
            control={control}
            label="Evacuation"
            name="evacuation"
            radioOptions={[
              { label: 'Yes', value: RadioGroupOption.Yes },
              { label: 'No', value: RadioGroupOption.No },
            ]}
            rules={{ required: true }}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
          <ControlledRadioGroup<SafetyEventReport>
            RadioGroupProps={{ row: true }}
            control={control}
            label="Emergency Egress Utilized"
            name="emergencyEgressUtilized"
            radioOptions={[
              { label: 'Yes', value: RadioGroupOption.Yes },
              { label: 'No', value: RadioGroupOption.No },
            ]}
            rules={{ required: true }}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
          <ControlledRadioGroup<SafetyEventReport>
            RadioGroupProps={{ row: true }}
            control={control}
            label="Crew Injury"
            name="crewInjury"
            radioOptions={[
              { label: 'Yes', value: RadioGroupOption.Yes },
              { label: 'No', value: RadioGroupOption.No },
            ]}
            rules={{ required: true }}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
          <ControlledRadioGroup<SafetyEventReport>
            RadioGroupProps={{ row: true }}
            control={control}
            label="Passenger Injury"
            name="passengerInjury"
            radioOptions={[
              { label: 'Yes', value: RadioGroupOption.Yes },
              { label: 'No', value: RadioGroupOption.No },
            ]}
            rules={{ required: true }}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
          <ControlledRadioGroup<SafetyEventReport>
            RadioGroupProps={{ row: true }}
            control={control}
            label="Ground Personnel Injury"
            name="groundPersonnelInjury"
            radioOptions={[
              { label: 'Yes', value: RadioGroupOption.Yes },
              { label: 'No', value: RadioGroupOption.No },
            ]}
            rules={{ required: true }}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
          <ControlledRadioGroup<SafetyEventReport>
            RadioGroupProps={{ row: true }}
            control={control}
            label="Aircraft Damage"
            name="aircraftDamage"
            radioOptions={[
              { label: 'Yes', value: RadioGroupOption.Yes },
              { label: 'No', value: RadioGroupOption.No },
            ]}
            rules={{ required: true }}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
          <ControlledRadioGroup<SafetyEventReport>
            RadioGroupProps={{ row: true }}
            control={control}
            label="Property Damage"
            name="propertyDamage"
            radioOptions={[
              { label: 'Yes', value: RadioGroupOption.Yes },
              { label: 'No', value: RadioGroupOption.No },
            ]}
            rules={{ required: true }}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
          <ControlledRadioGroup<SafetyEventReport>
            RadioGroupProps={{ row: true }}
            control={control}
            label="Adverse Passenger Reaction"
            name="adversePassengerReaction"
            radioOptions={[
              { label: 'Yes', value: RadioGroupOption.Yes },
              { label: 'No', value: RadioGroupOption.No },
            ]}
            rules={{ required: true }}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
          <ControlledRadioGroup<SafetyEventReport>
            RadioGroupProps={{ row: true }}
            control={control}
            label="Regulatory Compliance Concern"
            name="regulatoryComplianceConcern"
            radioOptions={[
              { label: 'Yes', value: RadioGroupOption.Yes },
              { label: 'No', value: RadioGroupOption.No },
            ]}
            rules={{ required: true }}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
          <ControlledRadioGroup<SafetyEventReport>
            RadioGroupProps={{ row: true }}
            control={control}
            label="Safety Concern"
            name="safetyComplianceConcern"
            radioOptions={[
              { label: 'Yes', value: RadioGroupOption.Yes },
              { label: 'No', value: RadioGroupOption.No },
            ]}
            rules={{ required: true }}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
          <ControlledRadioGroup<SafetyEventReport>
            RadioGroupProps={{ row: true }}
            control={control}
            label="FER Reminder"
            name="ferReminder"
            radioOptions={[
              { label: 'Yes', value: RadioGroupOption.Yes },
              { label: 'No', value: RadioGroupOption.No },
            ]}
            rules={{ required: true }}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
          <ControlledRadioGroup<SafetyEventReport>
            RadioGroupProps={{ row: true }}
            control={control}
            label="CIRP Info Offered"
            name="cirpInfoOffered"
            radioOptions={[
              { label: 'Yes', value: RadioGroupOption.Yes },
              { label: 'No', value: RadioGroupOption.No },
            ]}
            rules={{ required: true }}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} mt={2}>
        <Grid item xs={12}>
          <ControlledTextField<SafetyEventReport>
            name="additionalConsiderations"
            control={control}
            readOnly={readOnly}
            TextFieldProps={{
              multiline: true,
              rows: 3,
            }}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
      </Grid>

    </Collapse>
  );
}

export default EventStep;
